import { useCallback, useEffect, useMemo, useState } from 'react'
import { useAppDispatch, useAppSelector } from '../hooks/redux'
import {
  fetchFormTemplates,
  fetchFormTemplateDetail,
  createTemplateSection,
  updateTemplateSection,
  deleteTemplateSection,
  createTemplateField,
  updateTemplateField,
  deleteTemplateField,
  reorderTemplateFields,
  createTemplateLogicRule,
  updateTemplateLogicRule,
  deleteTemplateLogicRule,
  reorderTemplateLogicRules,
  createTemplate,
  deleteTemplate,
  updateTemplateMeta,
  createTemplateVersion,
  publishTemplateVersion,
} from '../store/slices/formTemplateSlice'
import { FormFieldType } from '@/types'
import type {
  FormFieldUpdatePayload,
  FormSectionUpdatePayload,
  FormTemplateScope,
  FormTemplateCreatePayload,
  FormTemplateUpdatePayload,
  FormTemplateVersionCreatePayload,
  FormTemplateSummary,
} from '@/types'
import type {
  CreateLogicRulePayload,
  UpdateLogicRulePayload,
} from '../services/api/formTemplates'
import FormTemplateEditor from '../components/forms/FormTemplateEditor'
import TemplateSidebar, { TemplateScopeFilter } from '../components/forms/TemplateSidebar'
import { useToast } from '../components/ToastContainer'
import TemplateCreateModal from '../components/forms/TemplateCreateModal'
import TemplateSettingsModal from '../components/forms/TemplateSettingsModal'
import VersionCreateModal from '../components/forms/VersionCreateModal'
import PublishVersionDialog from '../components/forms/PublishVersionDialog'
import TemplateSchedulePanel from '../components/forms/TemplateSchedulePanel'

const buildFieldPayload = (label: string) => {
  const normalized = label.trim() || '未命名欄位'
  const slug = normalized
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-'
    )
    .replace(/(^-|-$)/g, '')

  return {
    key: `${slug || 'field'}-${Date.now()}`,
    label: normalized,
    type: FormFieldType.SHORT_TEXT,
  }
}

const FormTemplateDesignerPage = () => {
  const dispatch = useAppDispatch()
  const { showError, showSuccess } = useToast()
  const {
    templates,
    loading,
    activeTemplate,
  } = useAppSelector((state) => state.formTemplates)

  const [scope, setScope] = useState<TemplateScopeFilter>('ALL')
  const [activeTemplateId, setActiveTemplateId] = useState<string | undefined>()
  const [activeVersionId, setActiveVersionId] = useState<string | undefined>()
  const [focusFieldKey, setFocusFieldKey] = useState<string | undefined>()
  const [createModalOpen, setCreateModalOpen] = useState(false)
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [versionModalOpen, setVersionModalOpen] = useState(false)
  const [publishDialogOpen, setPublishDialogOpen] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  const loadTemplates = useCallback(
    (currentScope: TemplateScopeFilter, search?: string) => {
      const params: { scopeType?: FormTemplateScope; search?: string } = {}
      if (currentScope !== 'ALL') {
        params.scopeType = currentScope as FormTemplateScope
      }
      if (search) {
        params.search = search
      }
      dispatch(fetchFormTemplates(params))
    },
    [dispatch],
  )

  useEffect(() => {
    loadTemplates(scope)
  }, [scope, loadTemplates])

  useEffect(() => {
    if (templates.length === 0) {
      setActiveTemplateId(undefined)
      setActiveVersionId(undefined)
      return
    }

    if (activeTemplateId) {
      const exists = templates.some((template) => template.id === activeTemplateId)
      if (!exists) {
        setActiveTemplateId(templates[0].id)
      }
    } else {
      setActiveTemplateId(templates[0].id)
    }
  }, [templates, activeTemplateId])

  useEffect(() => {
    if (!activeTemplateId) return
    dispatch(fetchFormTemplateDetail(activeTemplateId))
  }, [dispatch, activeTemplateId])

  useEffect(() => {
    if (!activeTemplate) {
      setActiveVersionId(undefined)
      return
    }
    if (activeTemplate.versions.length > 0) {
      setActiveVersionId(activeTemplate.versions[0].id)
    }
  }, [activeTemplate?.id])

  const handleScopeChange = (nextScope: TemplateScopeFilter) => {
    setScope(nextScope)
    setActiveTemplateId(undefined)
    setActiveVersionId(undefined)
    setFocusFieldKey(undefined)
  }

  const handleTemplateSelect = (templateId: string) => {
    setActiveTemplateId(templateId)
    setActiveVersionId(undefined)
    setFocusFieldKey(undefined)
  }

  const handleSearch = useCallback(
    (keyword: string) => {
      loadTemplates(scope, keyword)
    },
    [loadTemplates, scope],
  )

  const handleCreateSection = async (
    templateId: string,
    versionId: string,
    title: string,
  ) => {
    try {
      await dispatch(
        createTemplateSection({
          templateId,
          versionId,
          data: {
            title,
            position: (activeTemplate?.versions.find(v => v.id === versionId)?.sections.length ?? 0) + 1,
          },
        }),
      ).unwrap()
      showSuccess('節點已建立')
    } catch (error: any) {
      showError('建立節點失敗', error?.message || '請稍後再試')
    }
  }

  const handleUpdateSection = async (
    templateId: string,
    versionId: string,
    sectionId: string,
    payload: FormSectionUpdatePayload,
  ) => {
    try {
      await dispatch(
        updateTemplateSection({
          templateId,
          versionId,
          sectionId,
          data: payload,
        }),
      ).unwrap()
      showSuccess('節點資訊已更新')
    } catch (error: any) {
      showError('更新節點失敗', error?.message || '請稍後再試')
    }
  }

  const handleDeleteSection = async (
    templateId: string,
    versionId: string,
    sectionId: string,
  ) => {
    try {
      await dispatch(
        deleteTemplateSection({
          templateId,
          versionId,
          sectionId,
        }),
      ).unwrap()
      showSuccess('節點已刪除')
    } catch (error: any) {
      showError('刪除節點失敗', error?.message || '請稍後再試')
    }
  }

  const handleRenameField = async (
    templateId: string,
    versionId: string,
    fieldId: string,
    label: string,
  ) => {
    try {
      await dispatch(
        updateTemplateField({
          templateId,
          versionId,
          fieldId,
          data: { label },
        }),
      ).unwrap()
    } catch (error: any) {
      showError('更新欄位名稱失敗', error?.message || '請稍後再試')
      throw error
    }
  }

  const handleCreateTemplate = async (payload: FormTemplateCreatePayload) => {
    try {
      setSubmitting(true)
      const detail = await dispatch(createTemplate(payload)).unwrap()
      setActiveTemplateId(detail.id)
      setActiveVersionId(detail.versions[0]?.id)
      showSuccess('模板已建立')
      setCreateModalOpen(false)
    } catch (error: any) {
      showError('建立模板失敗', error?.message || '請稍後再試')
      throw error
    } finally {
      setSubmitting(false)
    }
  }

  const handleUpdateTemplateMeta = async (payload: FormTemplateUpdatePayload) => {
    if (!activeTemplateId) return
    try {
      setSubmitting(true)
      await dispatch(updateTemplateMeta({ templateId: activeTemplateId, data: payload })).unwrap()
      showSuccess('模板設定已更新')
      setSettingsOpen(false)
    } catch (error: any) {
      showError('更新模板設定失敗', error?.message || '請稍後再試')
      throw error
    } finally {
      setSubmitting(false)
    }
  }

  const handleDeleteTemplate = useCallback(
    async (template: FormTemplateSummary) => {
      if (submitting) return
      const confirmed = window.confirm(`確定要刪除模板「${template.name}」？此操作無法復原。`)
      if (!confirmed) return

      try {
        setSubmitting(true)
        await dispatch(deleteTemplate(template.id)).unwrap()
        showSuccess('模板已刪除')
        if (activeTemplateId === template.id) {
          setActiveTemplateId(undefined)
          setActiveVersionId(undefined)
        }
      } catch (error: any) {
        const message =
          typeof error === 'string'
            ? error
            : error?.message || '請稍後再試'
        showError('刪除模板失敗', message)
      } finally {
        setSubmitting(false)
      }
    },
    [submitting, dispatch, showSuccess, activeTemplateId, showError],
  )

  const handleCreateVersion = async (payload: FormTemplateVersionCreatePayload) => {
    if (!activeTemplateId) return
    try {
      setSubmitting(true)
      const version = await dispatch(
        createTemplateVersion({ templateId: activeTemplateId, data: payload }),
      ).unwrap()
      setActiveVersionId(version.id)
      showSuccess('新版本已建立')
      setVersionModalOpen(false)
    } catch (error: any) {
      showError('建立版本失敗', error?.message || '請稍後再試')
      throw error
    } finally {
      setSubmitting(false)
    }
  }

  const handlePublishVersion = async () => {
    if (!activeTemplateId || !activeVersionId) return
    try {
      setSubmitting(true)
      const version = await dispatch(
        publishTemplateVersion({
          templateId: activeTemplateId,
          versionId: activeVersionId,
          options: { archivePreviousVersions: true, markTemplatePublished: true },
        }),
      ).unwrap()
      showSuccess(`版本 ${version.displayName ?? `v${version.versionNumber}`} 已發佈`)
      setPublishDialogOpen(false)
    } catch (error: any) {
      showError('發佈版本失敗', error?.message || '請稍後再試')
      throw error
    } finally {
      setSubmitting(false)
    }
  }

  const handleCreateField = async (
    templateId: string,
    versionId: string,
    sectionId: string,
  ) => {
    const label = window.prompt('請輸入欄位名稱', '未命名欄位')
    if (label === null) return

    try {
      const fieldPayload = {
        ...buildFieldPayload(label),
        sectionId,
      }
      await dispatch(
        createTemplateField({
          templateId,
          versionId,
          data: fieldPayload,
        }),
      ).unwrap()
      setFocusFieldKey(fieldPayload.key)
      showSuccess('欄位已建立')
    } catch (error: any) {
      showError('建立欄位失敗', error?.message || '請稍後再試')
    }
  }

  const handleUpdateField = async (
    templateId: string,
    versionId: string,
    fieldId: string,
    payload: FormFieldUpdatePayload,
  ) => {
    await dispatch(
      updateTemplateField({
        templateId,
        versionId,
        fieldId,
        data: payload,
      }),
    ).unwrap()
  }

  const handleDeleteField = async (
    templateId: string,
    versionId: string,
    fieldId: string,
  ) => {
    await dispatch(
      deleteTemplateField({
        templateId,
        versionId,
        fieldId,
      }),
    ).unwrap()
  }

  const handleReorderFields = async (
    templateId: string,
    versionId: string,
    orderedIds: string[],
  ) => {
    await dispatch(
      reorderTemplateFields({ templateId, versionId, orderedIds }),
    ).unwrap()
  }

  const handleCreateLogicRule = async (
    templateId: string,
    versionId: string,
    payload: CreateLogicRulePayload,
  ) => {
    try {
      await dispatch(
        createTemplateLogicRule({ templateId, versionId, data: payload }),
      ).unwrap()
      showSuccess('條件邏輯已新增')
    } catch (error: any) {
      showError('新增條件邏輯失敗', error?.message || '請稍後再試')
    }
  }

  const handleUpdateLogicRule = async (
    templateId: string,
    versionId: string,
    ruleId: string,
    payload: UpdateLogicRulePayload,
  ) => {
    try {
      await dispatch(
        updateTemplateLogicRule({ templateId, versionId, ruleId, data: payload }),
      ).unwrap()
      showSuccess('條件邏輯已更新')
    } catch (error: any) {
      showError('更新條件邏輯失敗', error?.message || '請稍後再試')
      throw error
    }
  }

  const handleDeleteLogicRule = async (
    templateId: string,
    versionId: string,
    ruleId: string,
  ) => {
    try {
      await dispatch(
        deleteTemplateLogicRule({ templateId, versionId, ruleId }),
      ).unwrap()
      showSuccess('條件邏輯已刪除')
    } catch (error: any) {
      showError('刪除條件邏輯失敗', error?.message || '請稍後再試')
      throw error
    }
  }

  const handleReorderLogicRules = async (
    templateId: string,
    versionId: string,
    orderedRuleIds: string[],
  ) => {
    try {
      await dispatch(
        reorderTemplateLogicRules({ templateId, versionId, orderedRuleIds }),
      ).unwrap()
      showSuccess('條件邏輯排序已更新')
    } catch (error: any) {
      showError('條件邏輯排序失敗', error?.message || '請稍後再試')
      throw error
    }
  }

  const versionOptions = useMemo(
    () => activeTemplate?.versions ?? [],
    [activeTemplate?.versions],
  )

  const activeVersion = useMemo(
    () => versionOptions.find(version => version.id === activeVersionId) ?? versionOptions[0] ?? null,
    [versionOptions, activeVersionId],
  )

  return (
    <div className="flex h-full w-full">
      <TemplateSidebar
        templates={templates}
        loading={loading}
        activeTemplateId={activeTemplateId}
        scope={scope}
        onSelectTemplate={handleTemplateSelect}
        onRefresh={() => loadTemplates(scope)}
        onScopeChange={handleScopeChange}
        onSearch={handleSearch}
        onCreateTemplate={() => setCreateModalOpen(true)}
        onDeleteTemplate={handleDeleteTemplate}
      />

      <main className="flex-1 overflow-y-auto">
        <div className="flex flex-col gap-6 p-4">
          <FormTemplateEditor
            template={activeTemplate}
            activeVersionId={activeVersionId ?? versionOptions[0]?.id}
            onChangeVersion={setActiveVersionId}
            onCreateSection={handleCreateSection}
            onUpdateSection={handleUpdateSection}
            onDeleteSection={handleDeleteSection}
            onCreateField={handleCreateField}
            onRenameField={handleRenameField}
            onUpdateField={async (templateId, versionId, fieldId, payload) => {
              try {
                await handleUpdateField(templateId, versionId, fieldId, payload)
                showSuccess('欄位設定已更新')
              } catch (error: any) {
                showError('更新欄位失敗', error?.message || '請稍後再試')
              }
            }}
            onDeleteField={async (templateId, versionId, fieldId) => {
              try {
                await handleDeleteField(templateId, versionId, fieldId)
                showSuccess('欄位已刪除')
              } catch (error: any) {
                showError('刪除欄位失敗', error?.message || '請稍後再試')
              }
            }}
            onReorderFields={async (templateId, versionId, _sectionId, orderedIds) => {
              try {
                await handleReorderFields(templateId, versionId, orderedIds)
                showSuccess('欄位排序已更新')
              } catch (error: any) {
                showError('欄位排序更新失敗', error?.message || '請稍後再試')
              }
            }}
            focusFieldKey={focusFieldKey}
            onFocusFieldHandled={() => setFocusFieldKey(undefined)}
            onCreateLogicRule={async (templateId, versionId, payload) => {
              await handleCreateLogicRule(templateId, versionId, payload)
            }}
            onUpdateLogicRule={async (templateId, versionId, ruleId, payload) => {
              try {
                await handleUpdateLogicRule(templateId, versionId, ruleId, payload)
              } catch {
                // 錯誤已由 handler 顯示
              }
            }}
            onDeleteLogicRule={async (templateId, versionId, ruleId) => {
              try {
                await handleDeleteLogicRule(templateId, versionId, ruleId)
              } catch {
                // 已顯示錯誤
              }
            }}
            onReorderLogicRules={async (templateId, versionId, orderedRuleIds) => {
              try {
                await handleReorderLogicRules(templateId, versionId, orderedRuleIds)
              } catch {
                // 已顯示錯誤
              }
            }}
            onOpenTemplateSettings={() => setSettingsOpen(true)}
            onOpenCreateVersion={() => setVersionModalOpen(true)}
            onOpenPublishVersion={() => setPublishDialogOpen(true)}
            canPublishActiveVersion={Boolean(activeVersion && activeVersion.status !== 'PUBLISHED')}
          />

          {activeTemplate && (
            <TemplateSchedulePanel
              templateId={activeTemplate.id}
              templateVersionId={activeVersion?.id}
              projectId={activeTemplate.projectId ?? undefined}
              formType={activeTemplate.formType}
            />
          )}
        </div>
      </main>

      <TemplateCreateModal
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onSubmit={handleCreateTemplate}
        submitting={submitting}
      />
      <TemplateSettingsModal
        open={settingsOpen}
        template={activeTemplate}
        onClose={() => setSettingsOpen(false)}
        onSubmit={handleUpdateTemplateMeta}
        submitting={submitting}
      />
      <VersionCreateModal
        open={versionModalOpen}
        versions={versionOptions}
        onClose={() => setVersionModalOpen(false)}
        onSubmit={handleCreateVersion}
        submitting={submitting}
      />
      <PublishVersionDialog
        open={publishDialogOpen}
        versionLabel={activeVersion ? activeVersion.displayName ?? `v${activeVersion.versionNumber}` : ''}
        onClose={() => setPublishDialogOpen(false)}
        onConfirm={handlePublishVersion}
        submitting={submitting}
      />
    </div>
  )
}

export default FormTemplateDesignerPage
